import { RegisterComponent } from '@/common/register-component';
import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from '@stencil/core';
import { LANGUAGE_CODE, NylasEvent, NylasSchedulerConnector, NylasSchedulerErrorResponse } from '../../..';
import { NylasScheduling } from '../nylas-scheduling/nylas-scheduling';
import { debug } from '@/utils/utils';
import { ConfigSettings } from '@/stores/scheduler-store';
import i18next from '@/utils/i18n';
import { ThemeConfig } from '@nylas/core';

/**
 * The `nylas-cancelled-event-card` component is a UI component that displays the cancelled event card.
 *
 * @part ncec - The cancelled event card host.
 * @part ncec__icon - The calendar icon.
 * @part ncec__title - The title of the cancelled event card.
 * @part ncec__description - The description of the cancelled event card.
 * @part ncec__button-outline - The close button CTA.
 * @part ncec__card - The cancelled event card.
 */
@Component({
  tag: 'nylas-cancelled-event-card',
  styleUrl: 'nylas-cancelled-event-card.scss',
  shadow: true,
})
export class NylasCancelledEventCard {
  /**
   * The host element.
   * Used to manage the host element of the provider.
   */
  @Element() private host!: HTMLNylasCancelledEventCardElement;

  /**
   * @standalone
   * The config settings for the scheduler.
   */
  @Prop() readonly configSettings?: ConfigSettings;

  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: any;

  /**
   * The selected language.
   */
  @Prop({ attribute: 'selected-language' }) readonly selectedLanguage?: LANGUAGE_CODE;

  /**
   * The participant's name who booked the event / is logged in.
   */
  @Prop() readonly cancelledEventInfo!: Partial<NylasEvent>;

  /**
   * Closing state of the cancelled event card button.
   */
  @State() isClosing: boolean = false;

  /**
   * This event is fired when the close button is clicked on the cancelled event card.
   */
  @Event() readonly closeCancelEventCardClicked!: EventEmitter<{ errorHandler?: (error: NylasSchedulerErrorResponse) => void }>;

  /**
   * This event is fired when an error occurs.
   */
  @Event() readonly cancelledEventCardError!: EventEmitter<NylasSchedulerErrorResponse>;

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @Watch('themeConfig')
  themeConfigChanged(newThemeConfig: ThemeConfig) {
    this.applyThemeConfig(newThemeConfig);
  }

  connectedCallback() {
    debug(`[nylas-cancelled-event-card] Component connected`);
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  async componentWillLoad() {
    debug(`[nylas-cancelled-event-card] Component will load`);
  }

  async componentDidLoad() {
    debug(`[nylas-cancelled-event-card] Component did load`);
    if (!this.cancelledEventInfo) {
      debug(`[nylas-cancelled-event-card] No cancelled event info provided, "cancelledEventInfo" prop is required.`);
    }
  }

  disconnectedCallback() {
    debug(`[nylas-cancelled-event-card] Component disconnected`);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  private handleCloseClicked = () => {
    this.isClosing = true;
    const errorHandler = (error: NylasSchedulerErrorResponse) => {
      this.cancelledEventCardError.emit(error);
      this.isClosing = false;
    };
    this.closeCancelEventCardClicked.emit({ errorHandler });
  };

  @RegisterComponent<NylasCancelledEventCard, NylasSchedulerConnector, Exclude<NylasScheduling['stores'], undefined>>({
    name: 'nylas-cancelled-event-card',
    stateToProps: new Map([
      ['scheduler.configSettings', 'configSettings'],
      ['scheduler.themeConfig', 'themeConfig'],
      ['scheduler.selectedLanguage', 'selectedLanguage'],
    ]),
    eventToProps: {
      closeCancelEventCardClicked: async (_: CustomEvent<{ errorHandler?: (error: NylasSchedulerErrorResponse) => void }>, nylasschedulerconnector: NylasSchedulerConnector) => {
        const result = await nylasschedulerconnector.scheduler.resetCancel();

        const { errorHandler } = _.detail;
        if (errorHandler && (!result || 'error' in result)) {
          errorHandler(result);
        }
      },
      cancelledEventCardError: async (event: CustomEvent<NylasSchedulerErrorResponse>, _nylasschedulerconnector: NylasSchedulerConnector) => {
        debug('nylas-cancelled-event-card', 'cancelledEventCardError', event.detail);
      },
    },
    fireRegisterEvent: true,
  })
  render() {
    return (
      <Host part="ncec">
        <div class="nylas-cancelled-event-card" part="ncec__card">
          <div class="nylas-cancelled-event-card__calendar-icon" part="ncec__icon">
            <calendar-cancel-icon />
          </div>
          <h3 class="nylas-cancelled-event-card__title" part="ncec__title">
            {i18next.t('bookingCancelledTitle')}!
          </h3>
          <div class="nylas-cancelled-event-card__description" part="ncec__description">
            {i18next.t('bookingCancelledMessage')} {this.configSettings?.organizer?.name || i18next.t('bookingCancelledMessage_theParticipants')}.
          </div>
          <div class="nylas-cancelled-event-card__cta">
            <button-component variant="basic" disabled={this.isClosing} part="ncec__button-outline" onClick={this.handleCloseClicked}>
              {this.isClosing ? `${i18next.t('closingButton')}...` : `${i18next.t('closeButton')}`}
            </button-component>
          </div>
        </div>
      </Host>
    );
  }
}
